'use client';

import { createContext, type PropsWithChildren, useContext, useMemo } from 'react';

import type { DeferCarouselMode, GroupBoundaryMode } from './use-tool-group-state';

interface ToolGroupOverrides {
  deferCarousel?: DeferCarouselMode;
  ignoreGroupBoundaries?: GroupBoundaryMode;
}

type OverridesByTool = Record<string, ToolGroupOverrides>;

const ToolGroupOverridesContext = createContext<OverridesByTool | null>(null);

/**
 * Provides per-tool overrides for deferCarousel / ignoreGroupBoundaries.
 * Used by Storybook stories to make these settings controllable via args.
 *
 * @example
 * <ToolGroupOverridesProvider overrides={{ searchFlights: { deferCarousel: 'bottom' } }}>
 */
export function ToolGroupOverridesProvider({
  overrides,
  children,
}: PropsWithChildren<{ overrides: OverridesByTool }>) {
  const key = JSON.stringify(overrides);
  const stable = useMemo(() => overrides, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <ToolGroupOverridesContext.Provider value={stable}>
      {children}
    </ToolGroupOverridesContext.Provider>
  );
}

export function useToolGroupOverrides(toolName?: string): ToolGroupOverrides {
  const overrides = useContext(ToolGroupOverridesContext);
  if (!overrides || !toolName) return {};
  return overrides[toolName] ?? {};
}
